import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { counterOffer } from '../../../actions/jobsAction';
import { AlertSolidIcon } from '../../../images/icons/AlertSolidIcon';
import { Colors } from '../../../themes/colors';
import { NewCustomPopup } from '../../commonComponents/NewCustomPopup';

const generateRates = (min, max) => {
  const rates = [];
  for (let i = min; i <= max; i += 1) {
    rates.push(i);
  }
  return rates;
};

// Convert time string to minutes from midnight
const timeToMinutes = (timeStr) => {
  if (!timeStr) return null;

  // Handle 24-hour format (HH:MM)
  if (timeStr.match(/^\d{1,2}:\d{2}$/)) {
    const [hourStr, minuteStr] = timeStr.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);
    return hour * 60 + minute;
  }

  // Handle 12-hour format (HH:MM am/pm) for initial data
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(am|pm)$/i);
  if (!match) return null;

  const [, hourStr, minuteStr, period] = match;
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  if (period.toLowerCase() === 'pm' && hour !== 12) hour += 12;
  if (period.toLowerCase() === 'am' && hour === 12) hour = 0;

  return hour * 60 + minute;
};

// Convert minutes from midnight back to 24-hour time string
const minutesToTimeString = (minutes) => {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return '';

  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

// Convert minutes from midnight back to AM/PM format
const minutesToAmPmFormat = (minutes) => {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return '';

  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;

  const ampm = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour % 12 || 12;

  return `${displayHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${ampm}`;
};

export const JobConfirmOfferModal = ({ isShow, onClose, job, professional }) => {
  const dispatch = useDispatch();
  const {
    rate,
    local_start_time: localStartTime,
    local_end_time: localEndTime,
    local_date: localDate,
    id,
    lunch_break_time: lunchBreakTime,
  } = job || {};
  const initialBeginTimestamp = timeToMinutes(localStartTime);
  const initialEndTimestamp = timeToMinutes(localEndTime);

  const [formValues, setFormValues] = useState({
    rate: rate || '',
    begin: initialBeginTimestamp,
    end: initialEndTimestamp,
  });

  const [error, setError] = useState('');

  const rates = useMemo(() => generateRates(rate, 200), [rate]);

  const handleClose = () => {
    onClose();
    setFormValues({ rate: '', begin: null, end: null });
    setError('');
  };

  const validateField = (field, value, compareValue) => {
    // Check if any field has been changed from original values
    const isBeginChanged =
      field === 'begin'
        ? value !== initialBeginTimestamp
        : formValues.begin !== initialBeginTimestamp;
    const isEndChanged =
      field === 'end' ? value !== initialEndTimestamp : formValues.end !== initialEndTimestamp;
    const hasChanges = isBeginChanged || isEndChanged;

    // Check 7-day advance validation only if user is making changes
    if (hasChanges && localDate && localStartTime) {
      const shiftDate = new Date(localDate);
      const timeMatch = localStartTime.match(/^(\d{1,2}):(\d{2})\s?(am|pm)$/i);
      if (timeMatch) {
        const [, hourStr, minuteStr, period] = timeMatch;
        let hour = parseInt(hourStr, 10);
        const minute = parseInt(minuteStr, 10);

        if (period.toLowerCase() === 'pm' && hour !== 12) hour += 12;
        if (period.toLowerCase() === 'am' && hour === 12) hour = 0;

        shiftDate.setHours(hour, minute, 0, 0);
        const currentDate = new Date();
        const timeDifference = shiftDate.getTime() - currentDate.getTime();
        const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

        if (daysDifference > 7) {
          return 'You cannot counter the hours more than 7 days in advance of the shift start time';
        }
      }
    }

    const arrival = field === 'begin' ? value : compareValue;
    const departure = field === 'end' ? value : compareValue;
    const startTime = timeToMinutes(localStartTime);
    const endTime = timeToMinutes(localEndTime);

    let error = '';

    if (!value) return '';

    if (field === 'begin') {
      if (arrival === departure) {
        error = 'Arrival Time cannot be the same as Departure Time.';
      } else if (arrival < startTime) {
        error = 'Arrival Time cannot be earlier than shift start time.';
      } else if (arrival > endTime) {
        error = 'Arrival Time cannot be after shift end time.';
      } else if (compareValue && arrival > departure) {
        error = 'Arrival Time cannot be after Departure Time.';
      } else if (departure - arrival <= lunchBreakTime) {
        error = `Shift Time cannot be less than Meal Break Time (${lunchBreakTime} min).`;
      }
    }

    if (field === 'end') {
      if (arrival === departure) {
        error = 'Departure Time cannot be the same as Arrival Time.';
      } else if (departure > endTime) {
        error = 'Departure Time cannot be later than shift end time.';
      } else if (departure < startTime) {
        error = 'Departure Time cannot be earlier than shift start time.';
      } else if (compareValue && departure < arrival) {
        error = 'Departure Time cannot be before Arrival Time.';
      } else if (departure - arrival <= lunchBreakTime) {
        error = `Shift Time cannot be less than Meal Break Time (${lunchBreakTime} min).`;
      }
    }

    return error;
  };

  const handleSubmit = () => {
    const { rate: selectedRate, begin, end } = formValues;
    dispatch(
      counterOffer(id, {
        hygienistId: professional.id,
        rate: selectedRate,
        begin: minutesToAmPmFormat(begin),
        end: minutesToAmPmFormat(end),
      }),
    );
    handleClose();
  };

  if (!isShow) return null;

  return (
    <NewCustomPopup
      title="Confirm Offer"
      rightButtonText="Submit Offer"
      leftButtonText="Close"
      rightButtonPressed={handleSubmit}
      rightButtonDisabled={
        !formValues.rate || formValues.begin === null || formValues.end === null || !!error
      }
      onCloseIconClick={onClose}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          width: '80%',
          marginBottom: 20,
          marginTop: 20,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: 20,
            borderBottom: `1px solid ${Colors.neutral_100}`,
            paddingBottom: 20,
          }}
        >
          <strong
            style={{
              color: Colors.secondary_700,
              textTransform: 'uppercase',
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            Hygienist
          </strong>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column' }}
          >
            <span style={{ fontSize: 18, fontWeight: 500 }}>{professional.name}</span>
            <span style={{ fontSize: 14, fontWeight: 400 }}>{professional.address}</span>
          </div>
        </div>
        <CustomField
          label="Hourly Rate"
          value={formValues.rate}
          onChange={(value) => {
            setFormValues((prev) => ({ ...prev, rate: Number(value) }));
          }}
          options={rates.map((rate) => ({ title: `$${rate.toFixed(2)}`, value: rate }))}
        />
        <CustomField
          label="Arrival Time"
          value={minutesToTimeString(formValues.begin)}
          onChange={(timeString) => {
            const timestamp = timeToMinutes(timeString);
            setFormValues((prev) => ({ ...prev, begin: timestamp }));
            const beginError = validateField('begin', timestamp, formValues.end);
            setError(beginError);
          }}
          type="time"
        />
        <CustomField
          label="Departure Time"
          value={minutesToTimeString(formValues.end)}
          onChange={(timeString) => {
            const timestamp = timeToMinutes(timeString);
            setFormValues((prev) => ({ ...prev, end: timestamp }));
            const endError = validateField('end', timestamp, formValues.begin);
            setError(endError);
          }}
          type="time"
        />

        {error && (
          <div
            style={{
              color: Colors.error_500,
              fontSize: 14,
              marginTop: -6,
              marginBottom: 6,
              marginLeft: 16,
              display: 'flex',
              gap: 4,
            }}
          >
            <AlertSolidIcon />
            {error}
          </div>
        )}
      </div>
    </NewCustomPopup>
  );
};

const CustomField = ({ label, value, onChange, options, type }) => (
  <div
    style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <span className="col-6" style={{ fontWeight: '600' }}>
      {label}:
    </span>
    {type === 'time' ? (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        type="time"
        className="form-control"
        style={{
          fontSize: 14,
        }}
      />
    ) : (
      <select
        className="custom-select custom-select-lg"
        style={{ position: 'relative', fontSize: 14 }}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option className="dropdown-item" value="" disabled={!!value}>
          Select
        </option>
        {options?.map((option) => (
          <option key={option.value} className="dropdown-item" value={option.value}>
            {option.title}
          </option>
        ))}
      </select>
    )}
  </div>
);
